import React from 'react';
import styled, { css } from 'styled-components';
import { rgba } from 'polished';
import Label from './Label';
import { commonInputStyles, marketplaceInputStyles, marketplaceBlueInputStyles } from './Common';
import Error from './Error';
import COLORS from '../../../constants/COLORS';
import useNumericInput from '../../../hooks/useNumericInput';

interface Props {
  onChange: (name: string, value: number) => void;
  value: number;
  label?: string;
  type?: string;
  name?: string;
  error?: string;
  min?: number;
  isMobile?: boolean;
  marketplaceGrey?: boolean;
  max?: number;
}

const NumericInput = ({
  name,
  value,
  label,
  error,
  isMobile,
  marketplaceGrey,
  min,
  max,
  onChange,
}: Props) => {
  const {
    state,
    handleFocus,
    handleBlur,
    handleChange,
    handleKeyDown,
    onMouseEnter,
    onMouseLeave,
    decrementValue,
    incrementValue,
  } = useNumericInput({ onChange, name, max, min, value });
  const isMarketplace = marketplaceGrey;
  const marketplaceBlue = !marketplaceGrey;
  const hasValue = !!value || value === 0;

  return (
    <Wrapper isMobile={isMobile} marketplace={isMarketplace} data-selenium="numeric-input">
      {label && <Label active={hasValue || state.focused}>{label}</Label>}
      <InputWrapper
        isMobile={isMobile}
        marketplace={isMarketplace}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      >
        <MinusButton
          type="button"
          isMobile={isMobile}
          marketplace={isMarketplace}
          onClick={decrementValue}
          hovered={state.hovered}
          isDisabled={value === min}
          marketplaceBlue={marketplaceBlue}
        >
          -
        </MinusButton>
        <StyledInput
          name={name}
          value={value}
          onKeyDown={handleKeyDown}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          isMobile={isMobile}
          marketplace={isMarketplace}
          marketplaceBlue={marketplaceBlue}
        />
        <PlusButton
          type="button"
          isMobile={isMobile}
          marketplace={isMarketplace}
          onClick={incrementValue}
          hovered={state.hovered}
          isDisabled={value === max}
          marketplaceBlue={marketplaceBlue}
        >
          +
        </PlusButton>
      </InputWrapper>
      {error && <Error>{error}</Error>}
    </Wrapper>
  );
};

export default NumericInput;

const MobileWrapper = css`
  display: flex;
  align-items: center;
`;

interface WrapperProps {
  isMobile: boolean;
  marketplace?: boolean;
}

const Wrapper = styled.label<WrapperProps>`
  position: relative;
  display: block;

  @media screen and (max-width: 700px) {
    ${({ marketplace }) => !marketplace && MobileWrapper};
  }

  ${({ isMobile, marketplace }) => (!marketplace && isMobile ? MobileWrapper : null)};
`;

const MobileInputWrapper = css`
  display: flex;
  align-items: center;
  margin-left: 16px;

  ${(props) =>
    props.theme.isRtl &&
    css`
      margin-left: unset;
      margin-right: 16px;
    `}
`;

const MarketplaceMobileInputWrapper = css`
  padding-right: 84px;
  ${(props) =>
    props.theme.isRtl &&
    css`
      padding-right: unset;
      padding-left: 84px;
    `}
`;

interface InputWrapperProps {
  isMobile: boolean;
  marketplace?: boolean;
}

const InputWrapper = styled.div<InputWrapperProps>`
  position: relative;

  @media screen and (max-width: 700px) {
    ${({ marketplace }) => (marketplace ? MarketplaceMobileInputWrapper : MobileInputWrapper)};
  }

  ${({ marketplace, isMobile }) => (!marketplace && isMobile ? MobileInputWrapper : null)};
  ${({ marketplace, isMobile }) =>
    marketplace && isMobile ? MarketplaceMobileInputWrapper : null};
`;

const MobileStyledInput = css`
  margin: 0 4px;
  width: 58px;
  text-align: center;
`;

interface StyledInputProps {
  isMobile: boolean;
  marketplace?: boolean;
  marketplaceBlue: boolean;
}

const StyledInput = styled.input<StyledInputProps>`
  width: 100%;
  height: 40px;
  ${({ marketplace, isMobile }: StyledInputProps) =>
    marketplace && !isMobile ? marketplaceInputStyles : commonInputStyles}

  -moz-appearance: textfield;

  &::-webkit-inner-spin-button,
  &::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  @media screen and (max-width: 700px) {
    ${({ marketplace }: StyledInputProps) => !marketplace && MobileStyledInput};
  }

  ${({ isMobile, marketplace }) => (!marketplace && isMobile ? MobileStyledInput : null)};

  ${({ marketplaceBlue }: StyledInputProps) => marketplaceBlue && marketplaceBlueInputStyles};
`;

const MobileButton = css<{
  isDisabled?: boolean;
  marketplace?: boolean;
  marketplaceBlue?: boolean;
}>`
  position: static;
  height: 40px;
  font-size: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 0;
  border-radius: 8px;
  background: ${({ theme }) => rgba(theme.buttonBackground || COLORS.YELLOW, 0.8)};
  color: #fff;
  transition: all 0.2s ease-in-out;

  &:hover {
    background: ${({ theme }) => theme.buttonBackground};
  }

  ${({ marketplaceBlue, theme }) =>
    marketplaceBlue &&
    css`
      background: ${theme.buttonBackground};
    `}

  ${({ isDisabled, marketplaceBlue }) =>
    isDisabled &&
    marketplaceBlue &&
    css`
      background: ${COLORS.MARKETPLACE_GREY};
      color: rgba(21, 16, 53, 0.4);

      &:hover {
        background: ${COLORS.MARKETPLACE_GREY};
        color: rgba(21, 16, 53, 0.4);
      }
    `}
`;

const MarketplaceMobileButton = css`
  width: 40px;
  font-weight: 900;
  font-size: 18px;
  color: rgba(21, 16, 53, 0.4);
`;

interface ButtonProps {
  isMobile: boolean;
  marketplace?: boolean;
  hovered?: boolean;
  isDisabled?: boolean;
  marketplaceBlue?: boolean;
}

const Button = styled.button<ButtonProps>`
  position: absolute;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 40px;
  height: 24px;
  right: 0;
  font-size: 14px;
  line-height: 18px;
  color: rgba(0, 0, 0, 0.8);
  background: #ffffff;
  border: 1px solid ${COLORS.MARKETPLACE_DARK_GREY};
  box-sizing: border-box;
  text-align: center;
  z-index: 1;
  transition: all 0.2s ease-in-out;

  ${(props) =>
    props.theme.isRtl &&
    css`
      right: unset;
      left: 0;
    `}

  &:hover {
    background: ${({ marketplace, theme }) =>
      marketplace ? COLORS.MARKETPLACE_GREY : rgba(theme.buttonBackground || COLORS.YELLOW, 0.24)};
    border-color: ${({ marketplace, color }) => (marketplace ? COLORS.MARKETPLACE_BLACK : color)};
    z-index: 2;
    cursor: pointer;
  }

  ${({ hovered, marketplace }) =>
    hovered &&
    marketplace &&
    css`
      border-color: ${COLORS.MARKETPLACE_BLACK};
    `}

  &::selection {
    background: transparent;
  }

  @media screen and (max-width: 700px) {
    height: 100%;
    ${({ marketplace }) => (marketplace ? MarketplaceMobileButton : MobileButton)};
  }

  ${({ isMobile, marketplace }) => (!marketplace && isMobile ? MobileButton : null)};
  ${({ isMobile, marketplace }) => (marketplace && isMobile ? MarketplaceMobileButton : null)};

  ${({ isDisabled, marketplaceBlue }) =>
    isDisabled &&
    marketplaceBlue &&
    css`
      background: ${COLORS.MARKETPLACE_GREY};
      color: rgba(21, 16, 53, 0.4);

      &:hover {
        background: ${COLORS.MARKETPLACE_GREY};
        color: rgba(21, 16, 53, 0.4);
      }
    `}
`;

const PlusButton = styled(Button)<ButtonProps>`
  top: 0;
`;

const MobileMinusButton = css`
  border-bottom-right-radius: 8px;

  ${(props) =>
    props.theme.isRtl &&
    css`
      border-bottom-left-radius: 8px;
    `}
`;

const MarketplaceMobileMinusButton = css`
  height: 100%;
  right: 42px;
  bottom: 0;

  ${(props) =>
    props.theme.isRtl &&
    css`
      right: unset;
      left: 42px;
    `}
`;

const MinusButton = styled(Button)<ButtonProps>`
  bottom: 0;
  border-bottom-right-radius: 2px;

  ${(props) =>
    props.theme.isRtl &&
    css`
      border-bottom-right-radius: unset;
      border-bottom-left-radius: 2px;
    `}

  @media screen and (max-width: 700px) {
    ${({ marketplace }) => (marketplace ? MarketplaceMobileMinusButton : MobileMinusButton)};
  }

  ${({ isMobile, marketplace }) => (!marketplace && isMobile ? MobileMinusButton : null)};
  ${({ isMobile, marketplace }) => (marketplace && isMobile ? MarketplaceMobileMinusButton : null)};
`;
